import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import clsx from "clsx";
import { useState } from "react";
import {
  AnimatedGradientBorder,
  GlowCard,
} from "screens/Landing/components/shared";
import { shadowAndBorderClass, gradientRowBgClass } from "../utils/classes";
import { useUserStore } from "store/user";
import { useTranslation } from "react-i18next";

interface TokenData {
  purchased: {
    amount: string;
    usdValue: string;
    tokenName: string;
    subtitle: string;
  };
  bonus: {
    amount: string;
    description: string;
  };
}

const mockTokenData: TokenData = {
  purchased: {
    amount: "10,000.00",
    usdValue: "$76.34",
    tokenName: "ATTS",
    subtitle: "AGNT ATTS",
  },
  bonus: {
    amount: "493.09 ATTS",
    description: "Earn bonuses for purchasing tokens",
  },
};

export default function TokensToReceive() {
  const { t } = useTranslation();
  const [tokenData] = useState<TokenData>(mockTokenData);
  const { user } = useUserStore();

  const totalBonus =
    user.purchasedTokens && user.totalTokensHeld
      ? user.totalTokensHeld - user.purchasedTokens
      : 0;

  return (
    <div className="max-lg:w-full w-full max-lg:mb-[64px]">
      <AnimatedGradientBorder className="rounded-[28px] max-lg:rounded-[24px] w-full">
        <GlowCard className="bg-[#111111] rounded-[28px] max-lg:rounded-[24px] before:!rounded-[28px] max-lg:before:!rounded-[24px] p-[12px] max-lg:p-[12px] flex flex-col gap-[12px] max-lg:gap-[8px]">
          {/* Title row */}
          <div
            className={clsx(
              "flex h-[47px] max-lg:h-[47px] px-5 max-lg:px-[8px] items-center rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px]",
              gradientRowBgClass,
            )}
          >
            <h2 className="text-[24px] max-lg:text-20m font-luxora-medium leading-[110%] text-white">
              {t("Tokens to be received")}
            </h2>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 max-lg:gap-[8px] lg:gap-[12px]">
            {/* Your Purchased ATTS */}
            <div
              className={clsx(
                "flex flex-col justify-between bg-[#161616] rounded-[16px] max-lg:rounded-[24px] before:!rounded-[16px] max-lg:before:!rounded-[24px] p-[12px] max-lg:p-[12px] max-lg:gap-[12px] lg:h-[160px]",
                shadowAndBorderClass,
              )}
            >
              <h3 className="text-[20px] p-[8px] max-lg:p-[8px] max-lg:text-[20px] font-luxora-medium text-white leading-[110%]">
                {t("Your Purchased ATTS")}
              </h3>

              <div
                className={clsx(
                  "flex items-center justify-between rounded-[16px] max-lg:rounded-[16px] lg:h-[75px] max-lg:before:!rounded-[16px] bg-[#252525] p-[12px] pr-[16px] max-lg:p-[8px]",
                  shadowAndBorderClass,
                )}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={clsx(
                      "w-[52px] h-[52px] max-lg:w-[52px] max-lg:h-[52px] rounded-full before:!rounded-full flex items-center justify-center",
                      shadowAndBorderClass,
                    )}
                  >
                    <AttsIcon className="w-[52px] h-[52px] max-lg:w-[52px] max-lg:h-[52px]" />
                  </div>

                  <div>
                    <div className="text-[20px] mb-[4px] max-lg:mb-[4px] max-lg:text-20m font-luxora-medium leading-[110%] text-white">
                      {tokenData.purchased.tokenName}
                    </div>
                    <div className="text-16 max-lg:text-16m font-luxora-regular leading-[110%] text-white/40">
                      {tokenData.purchased.subtitle}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-[20px] mb-[4px] max-lg:mb-[4px] max-lg:text-20m font-luxora-medium leading-[110%] text-white">
                    {user?.purchasedTokens
                      ? user.purchasedTokens.toFixed(2)
                      : "0.00"}
                  </div>
                  <p className="text-16 max-lg:text-16m font-luxora-regular leading-[110%] text-white/40">
                    {t("Amount")}
                  </p>
                </div>
              </div>
            </div>

            {/* Your Bonus Tokens */}
            <div
              className={clsx(
                "flex flex-col justify-between bg-[#161616] rounded-[16px] max-lg:rounded-[24px] before:!rounded-[16px] max-lg:before:!rounded-[24px] p-[12px] max-lg:p-[12px] lg:h-[160px]",
                shadowAndBorderClass,
              )}
            >
              <div className="flex flex-col p-[8px] max-lg:p-[8px] mb-[8px] max-lg:mb-[8px]">
                <h3 className="text-[20px] mb-[4px] max-lg:mb-[4px] max-lg:text-20m leading-[110%] font-luxora-medium text-white">
                  {t("Your Bonus Tokens")}
                </h3>
                <p className="text-[16px] max-lg:text-16m font-luxora-regular leading-[110%] text-white/40">
                  {t("earnBonusesForPurchasing")}
                </p>
              </div>

              <div
                className={clsx(
                  "flex items-center max-lg:mt-[8px] justify-between rounded-[16px] before:!rounded-[16px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] bg-[#252525] max-lg:h-[46px] lg:h-[75px] p-[12px] max-lg:p-[12px] max-lg:pr-[20px] pr-[16px]",
                  shadowAndBorderClass,
                )}
              >
                <div className="text-right">
                  <div className="text-[20px] max-lg:text-20m font-luxora-medium leading-[110%] text-white">
                    {totalBonus?.toFixed(2)} {tokenData.purchased.tokenName}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </GlowCard>
      </AnimatedGradientBorder>
    </div>
  );
}
